import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '@environment';
import { AuthService } from '@frontendlib/common/services/auth.service';
import { SignalsService } from '@frontendlib/common/services/signals.service';
import { getCsrfToken } from '@frontendlib/common/utils/auth';
import { spaceIdFromRouter } from '@modules/common/utils/space-route';
import { SignalType } from '@sharedlib/common/types/signal-type';
import { distinctUntilChanged, filter, map, skip, startWith, type Subscription } from 'rxjs';

import { openWorkbookInEditor } from '../open-workbook';
import { SheetsAppClient } from './sheets-app-client';

const clientUrl = (id: string) => `${environment.baseUrl}/api/sheets/clients/${id}`;

/**
 * The app shell's listener for the MCP contract's (6 Oct) "open a workbook" with no editor open (see
 * `SheetsAppClient`). Started by the signed-in layout, so it runs whichever app the tab shows, and it imports
 * nothing of the editor: the editor is the lazy `/sheets/open` route, loaded only when the route is taken.
 */
@Injectable({ providedIn: 'root' })
export class SheetsAppClientService {
  private readonly http = inject(HttpClient);
  private readonly signals = inject(SignalsService);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly zone = inject(NgZone);
  private readonly document = inject(DOCUMENT);
  private client: SheetsAppClient | null = null;
  /** What `stop` undoes: the subscriptions and the page's listeners. */
  private readonly teardown: (() => void)[] = [];

  /** Idempotent: the layout calls it each time it is built. */
  public start(): void {
    if (this.client) return;
    const client = new SheetsAppClient({
      // Per page load, in memory only (see SheetsAppClient on duplicated tabs).
      clientId: crypto.randomUUID(),
      now: () => Date.now(),
      report: (activeAt) =>
        this.http.post(clientUrl(client.id), { activeAt: new Date(activeAt).toISOString() }).subscribe({ error: () => undefined }),
      close: (beacon) => this.close(client.id, beacon),
      ack: (ack) => this.http.post(`${clientUrl(client.id)}/ack`, ack).subscribe({ error: () => undefined }),
      // "Open" in the workbook's menu: the editor's route, in this tab.
      openWorkbook: (id, sheetId) => this.zone.run(() => openWorkbookInEditor(this.router, id, sheetId)),
      url: () => this.router.url,
      setTimer: (ms, fire) => this.zone.runOutsideAngular(() => setTimeout(fire, ms)),
      clearTimer: (timer) => clearTimeout(timer as ReturnType<typeof setTimeout>),
    });
    this.client = client;
    const view = this.document.defaultView;
    const used = () => client.used();
    // Focus in the page's own frame (an email's body) is still this document's: coming back from it is a use, not
    // a return to the tab. Only a window the person really left (hasFocus false after the blur) reports on focus.
    let away = !this.document.hasFocus();
    const left = () => this.zone.runOutsideAngular(() => setTimeout(() => (away = !this.document.hasFocus())));
    const focused = () => {
      if (!away) return client.used();
      away = false;
      client.touched();
    };
    const visible = () => {
      if (this.document.visibilityState === 'visible') client.touched();
    };
    const gone = () => client.stop(true);
    const listen = (target: EventTarget | null, type: string, listener: () => void, options?: AddEventListenerOptions) => {
      this.zone.runOutsideAngular(() => target?.addEventListener(type, listener, options));
      this.teardown.push(() => target?.removeEventListener(type, listener, options));
    };
    listen(view, 'blur', left);
    listen(view, 'focus', focused);
    // A click or a key only stamps the time; the next report carries it (no request per click or keystroke).
    listen(view, 'pointerdown', used, { capture: true, passive: true });
    listen(view, 'keydown', used, { capture: true, passive: true });
    listen(this.document, 'visibilitychange', visible);
    listen(view, 'pagehide', gone);
    const subscriptions: Subscription[] = [
      this.signals.getSignal(SignalType.SHEETS_NAV_COMMAND).subscribe((signal) => void client.handle(signal)),
      // A navigation that moved the tab to another space; one inside the space (opening an email) is not news.
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          map(() => spaceIdFromRouter(this.router)),
          startWith(spaceIdFromRouter(this.router)),
          distinctUntilChanged(),
          skip(1),
        )
        .subscribe(() => client.moved()),
    ];
    this.teardown.push(() => subscriptions.forEach((s) => s.unsubscribe()));
    client.start(this.document.hasFocus());
    // Last: the current value comes at once, and signed out is the end of it.
    const signedOut = this.auth
      .getAuthenticated()
      .pipe(filter((payload) => !payload))
      .subscribe(() => this.stop());
    this.teardown.push(() => signedOut.unsubscribe());
  }

  /** Signed out: the tab is no longer one of the user's clients. */
  public stop(): void {
    const client = this.client;
    this.client = null;
    client?.stop(false);
    for (const undo of this.teardown.splice(0)) undo();
  }

  /**
   * As the editor session's close (session-http.ts): a beacon on `pagehide`, the CSRF token in a `text/plain` body;
   * otherwise, or when the browser refuses the beacon, an ordinary request.
   */
  private close(id: string, beacon: boolean): void {
    const url = `${clientUrl(id)}/close`;
    if (beacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const body = new Blob([JSON.stringify({ csrfToken: getCsrfToken() })], { type: 'text/plain' });
      if (navigator.sendBeacon(url, body)) return;
    }
    this.http.post(url, {}).subscribe({ error: () => undefined });
  }
}
